import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isNull, keyBy } from 'lodash-es';
import { useObservableState } from 'observable-hooks';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { map, of } from 'rxjs';
import { array, object, string } from 'yup';
import classNames from '../../../../utils/classNames';
import { getDistanceUnit } from '../../../../utils/formatting';
import { applyNamingConvention } from '../../../../utils/naming-conventions';
import { PLACEHOLDER_ROW_STRATEGY, RowManager } from '../../../../utils/row-manager';
import Input, { INPUT_SIZE } from '../../../form/Input';
import InputOrTextArea from '../../../form/InputOrTextArea';
import Select from '../../../form/Select';
import {
    defaultPropTypes,
    useIsOverridable,
    useIsOverridden,
    useWatchObservable,
} from '../../utils';
import { buildYupSchema } from '../../validation';
import FormFieldContainer from '../FormFieldContainer';
import { GenericRowField, inlineFieldGroupClasses } from '../genericRowField/GenericRowField';
import { fieldTypeToInputType, isNumberType, OverridableInput } from '../InputField';
import { AlarmRowManager } from './alarm-row-manager';
import {
    createAlarmRecipientPlaceholder,
    createInvalidEmailOrPhoneText,
    isEmailValid,
    isPhoneNumberValid,
} from './utils';

const borderColorClasses = ['border-input-green', 'border-input-orange', 'border-input-red'];

function getIdentity(recipient) {
    return recipient.email ?? recipient.phoneNumber;
}

function SubController({
    extraValidators,
    rules,
    subName,
    fieldOptions,
    render,
    mergeValidations,
    ...rest
}) {
    const { settings } = fieldOptions;

    const validators = rules.buildValidationSchema();
    if (mergeValidations) {
        /*
        Some components render multiple parts of the data the Controller
        keeps track of and validates. These multiple parts need to be on
        root level of the extraValidators. For example:
        extraValidators = {
          'emailDelay': EmailDelayValidator,
        }
        validators = {
          'lmax_alarms': LmaxAlarmValidators,
          'leq_alars': LeqAlarmValidatots,
        }
        In this cse the extra validators ends up as:
        extraValidators = {
          'emailDelay': EmailDelayValidator,
          'lmax_alarms': LmaxAlarmValidators,
          'leq_alars': LeqAlarmValidatots,
        }
          */
        Object.assign(extraValidators, validators);
    } else {
        /*
        Example:
        subName = 'alarmRecipients'
        extraValidators = {
          'emailDelay': EmailDelayValidator,
        }
        validators = AlarmRecipientsValidator
        In this cse the extra validators ends up as:
        extraValidators = {
          'emailDelay': EmailDelayValidator,
          'alarmRecipients': AlarmRecipientsValidator,
        }
          */
        extraValidators[subName] = validators;
    }
    return (
        <Controller
            {...rest}
            {...fieldOptions}
            defaultValue={settings.default}
            render={({ field: { value, onChange }, fieldState: { error } }) =>
                render({
                    field: {
                        // In this SubController we only expose the subName part of the
                        // structure to the render.
                        value: value[subName] || '',
                        onChange: (event) => {
                            // If this is an event, grab the value out of the target.
                            if (event.target) {
                                onChange({ ...value, [subName]: event.target.value });
                            } else {
                                onChange(event);
                            }
                        },
                        settings: value,
                    },
                    fieldState: { error: error?.[subName], allErrors: error },
                })
            }
        />
    );
}

function AlarmFieldInput({
    index,
    humanIndex,
    placeholder,
    ariaLabel,
    fieldType,
    fieldName,
    onChange,
    ...props
}) {
    const ref = useRef(null);
    const conditions$ = useWatchObservable([
        'atopEnabled',
        'buildingLevel',
        'atopFlatLevel',
        'flatLevel',
    ]);

    const [helpers] = useObservableState(
        () =>
            fieldName === 'alarmLevel'
                ? conditions$.pipe(
                      map(({ atopEnabled, buildingLevel, atopFlatLevel, flatLevel }) => [
                          // Vtop appears when the line is considered "flat".
                          {
                              show: buildingLevel === 'unspecified',
                              level: flatLevel,
                              id: `${props.id}_v-top`,
                              helpText: gettext('ALARM_LEVEL_VTOP'),
                              unit: 's',
                          },
                          // Atop appears when atop_enabled is True.
                          {
                              show: atopEnabled,
                              level: atopFlatLevel,
                              id: `${props.id}_a-top`,
                              helpText: gettext('ALARM_LEVEL_ATOP'),
                              unit: 's^2',
                          },
                      ])
                  )
                : of([]),
        []
    );

    return (
        <>
            <InputOrTextArea
                ref={ref}
                {...props}
                onChange={onChange}
                // Only add the alarm level placeholder to the title input,
                // which is currently always located at index 0.
                placeholder={index === 0 ? placeholder.replace('{index}', humanIndex) : null}
                aria-label={ariaLabel}
                type={fieldName === 'smsTemplate' ? 'textarea' : fieldTypeToInputType(fieldType)}
                maxLength={fieldName === 'smsTemplate' ? 80 : undefined}
            />
            {helpers
                .filter((helper) => helper.show)
                .map(({ level, id, helpText, unit }) => (
                    <div className="my-2" key={id}>
                        <Input
                            // props.value is a percent, so we evaluate (props.value/100 ) * level.
                            value={props.value ? Number(props.value) * level * 0.01 : 0}
                            onChange={(e) => {
                                // Convert the value back to a percentage.
                                const value = (e.target.valueAsNumber * 100) / level;
                                onChange({
                                    ...e,
                                    target: { ...ref.current, value, valueAsNumber: value },
                                });
                            }}
                            type="number"
                            id={id}
                        />
                        <label className="text-[11px]" htmlFor={id}>
                            {interpolate(applyNamingConvention(helpText), [
                                `${getDistanceUnit()}/${unit}`,
                                level / 100, // 1% of the flat level is the min value.
                            ])}
                        </label>
                    </div>
                ))}
        </>
    );
}

AlarmFieldInput.propTypes = {
    index: PropTypes.number.isRequired,
    humanIndex: PropTypes.number.isRequired,
    placeholder: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onChange: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string.isRequired,
    fieldType: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
};

SubController.propTypes = {
    extraValidators: PropTypes.object.isRequired,
    rules: PropTypes.shape({
        buildValidationSchema: PropTypes.func.isRequired,
    }),
    subName: PropTypes.string.isRequired,
    ...defaultPropTypes,
    render: PropTypes.func.isRequired,
    mergeValidations: PropTypes.bool,
    rest: PropTypes.object,
};

function AlarmLevelSettingsRow({ alarm, value, onChange, error }) {
    const { name, title, limit, placeholder, keyTemplate } = alarm;
    const overrideName = `notificationConfig.${name}`;
    const overridden = useIsOverridden(overrideName);
    const disabled = useIsOverridable(overrideName) && !overridden;

    const showColors = alarm.showColors ?? false;

    const emptyRow = useMemo(
        () => Object.fromEntries(alarm.fields.map((field) => [field.name, field.default])),
        [alarm.fields]
    );

    const extraManagerConfig = useMemo(
        () => ({ alarmName: name, keyTemplate }),
        [name, keyTemplate]
    );

    const fields = useMemo(
        () =>
            alarm.fields.map((field, index) => ({
                name: field.name,
                label: field.label,
                helpText: field.help_text,
                render(props) {
                    // Add 1 to make it a human index.
                    // eslint-disable-next-line react/prop-types
                    const humanIndex = props.index + 1;
                    const ariaLabel = `${field.label} ${humanIndex}`;

                    if (field.type === 'char' || isNumberType(field.type)) {
                        return (
                            <>
                                <AlarmFieldInput
                                    {...props}
                                    humanIndex={humanIndex}
                                    placeholder={placeholder}
                                    ariaLabel={ariaLabel}
                                    fieldType={field.type}
                                    fieldName={field.name}
                                />
                                {showColors && index === 0 && (
                                    <div
                                        className={classNames(
                                            'border',
                                            // eslint-disable-next-line react/prop-types
                                            borderColorClasses[props.index]
                                        )}
                                    ></div>
                                )}
                            </>
                        );
                    }

                    if (field.type === 'choice') {
                        return <Select {...props} data={field.options} aria-label={ariaLabel} />;
                    }

                    return new Error(
                        `AlarmSettingsField could not find field input for type '${field.type}'.`
                    );
                },
            })),
        [alarm.fields, showColors, placeholder]
    );

    return (
        <FormFieldContainer
            fieldOptions={{
                name: overrideName,
                settings: {
                    // Although the label is hidden, it is used by the
                    // aria-label of the override checkbox.
                    label: title,
                },
            }}
            showLabel={false}
        >
            <GenericRowField
                name={overrideName}
                fields={fields}
                emptyRow={emptyRow}
                maxRows={limit ?? Infinity}
                value={value}
                error={error}
                onChange={onChange}
                disabled={disabled}
                Manager={AlarmRowManager}
                extraConfig={extraManagerConfig}
            />
        </FormFieldContainer>
    );
}

AlarmLevelSettingsRow.propTypes = {
    alarm: PropTypes.shape({
        name: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        limit: PropTypes.number.isRequired,
        placeholder: PropTypes.string.isRequired,
        keyTemplate: PropTypes.string.isRequired,
        showColors: PropTypes.bool,
        fields: PropTypes.array.isRequired,
    }),
    value: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.array,
};

function AlarmLevelSettingsField({ fieldOptions, extraValidators }) {
    const {
        settings: { alarms },
    } = fieldOptions;
    const subName = 'alarms';

    return (
        <SubController
            extraValidators={extraValidators}
            subName={subName}
            fieldOptions={fieldOptions}
            mergeValidations={true}
            rules={{
                buildValidationSchema() {
                    return Object.fromEntries(
                        alarms.map((alarm) => [
                            alarm.name,
                            array(buildYupSchema(keyBy(alarm.fields, 'name'))),
                        ])
                    );
                },
            }}
            render={({ field: { onChange, settings }, fieldState: { allErrors } }) => (
                <>
                    {alarms.map((alarm, index) => (
                        // The ID is needed for the walkthrough.
                        <div key={alarm.name} id={index === 0 ? 'alarmLevels' : null}>
                            <h3 className="text-black">{alarm.title}</h3>
                            <AlarmLevelSettingsRow
                                alarm={alarm}
                                value={settings}
                                onChange={onChange}
                                error={allErrors?.[alarm.name]}
                            />
                        </div>
                    ))}
                </>
            )}
        />
    );
}

AlarmLevelSettingsField.propTypes = {
    ...defaultPropTypes,
    extraValidators: PropTypes.object.isRequired,
};

function RowElement({ index, disabled, configuredAlarms, row, manager, error, overrideName }) {
    const humanIndex = index + 1;
    const enabledAlarmsError = error?.enabledAlarms;
    const identityError = error?.identity;

    return (
        <tr>
            <td className="w-56 pr-3">
                {/*
                Because the values of registered fields that are disabled do
                not come back with `getValues()` or `watch()` we have to do a
                hack here that creates an extra fake disabled field when disabled.
                This allows us to simply hide the original field and keep everything
                working as expected.
                */}
                <OverridableInput
                    type="text"
                    placeholder={createAlarmRecipientPlaceholder()}
                    aria-label={interpolate(
                        gettext('RECIPIENT_INPUT_INDEX'),
                        { index: humanIndex },
                        true
                    )}
                    size={INPUT_SIZE.MEDIUM}
                    error={!!identityError}
                    disabled={disabled}
                    overrideName={overrideName}
                    value={getIdentity(row) ?? ''}
                    onChange={(e) => {
                        /*
                        Trim the value as soon as it changes. There's no need to inform the
                        user that they didn't enter a valid value, as trimming is plainly
                        sanitizing the input.
                        */
                        const toBeDecided = (e.target.value ?? '').trim();
                        let email = null;
                        let phoneNumber = null;

                        if (toBeDecided) {
                            if (isEmailValid(toBeDecided)) {
                                email = toBeDecided;
                            } else {
                                phoneNumber = toBeDecided;
                            }
                        }

                        manager.changeRow(index, { email, phoneNumber });
                    }}
                />
                {identityError && (
                    <span className="text-xs text-form-warning">{identityError.message}</span>
                )}
            </td>
            {configuredAlarms.map(({ key, title }) => (
                <td className={`w-10 p-0 text-center align-top `} key={key}>
                    <div
                        className={`flex h-10 items-center justify-center border-l border-[#ccc] ${
                            enabledAlarmsError && 'bg-form-warning'
                        }`}
                    >
                        <OverridableInput
                            type="checkbox"
                            aria-label={interpolate(
                                gettext('RECIPIENT_CHECKBOX_INDEX'),
                                {
                                    title,
                                    index: humanIndex,
                                },
                                true
                            )}
                            disabled={disabled}
                            overrideName={overrideName}
                            checked={row.enabledAlarms.includes(key)}
                            onChange={(e) => {
                                let newValue = [...row.enabledAlarms];

                                if (e.target.checked) {
                                    !newValue.includes(key) && newValue.push(key);
                                } else {
                                    newValue.includes(key) &&
                                        (newValue = newValue.filter((alarm) => alarm !== key));
                                }

                                manager.changeRow(index, 'enabledAlarms', newValue);
                            }}
                        />
                    </div>
                </td>
            ))}
        </tr>
    );
}

RowElement.propTypes = {
    index: PropTypes.number.isRequired,
    disabled: PropTypes.bool.isRequired,
    configuredAlarms: PropTypes.array.isRequired,
    row: PropTypes.shape({
        email: PropTypes.string,
        phoneNumber: PropTypes.string,
        enabledAlarms: PropTypes.array.isRequired,
    }).isRequired,
    manager: PropTypes.instanceOf(RowManager).isRequired,
    error: PropTypes.object,
    overrideName: PropTypes.string.isRequired,
};

const emptyRecipientRow = {
    enabledAlarms: [],
    email: null,
    phoneNumber: null,
    webhook: null,
};

function isValidContactInfo(test, validationFunction) {
    return isNull(test) || test === '' || validationFunction(test);
}

function AlarmRecipientsField({ fieldOptions, extraValidators }) {
    const {
        settings: { alarms },
    } = fieldOptions;
    const subName = 'recipients';
    const overrideName = `notificationConfig.${subName}`;
    const overridden = useIsOverridden(overrideName);
    const disabled = useIsOverridable(overrideName) && !overridden;

    return (
        <SubController
            extraValidators={extraValidators}
            subName={subName}
            fieldOptions={fieldOptions}
            rules={{
                buildValidationSchema() {
                    return array(
                        object({
                            email: string().nullable(),
                            phoneNumber: string().nullable(),
                            enabledAlarms: array().when(['email', 'phoneNumber'], {
                                is: (email, phoneNumber) => email ?? phoneNumber,
                                then: (schema) =>
                                    schema.min(
                                        1,
                                        /* Not translated as this message is not visible to
                                        the user. */
                                        'There must be at least one enabled alarm if email or phone number is provided.'
                                    ),
                            }),
                        }).test('identity', null, function validateIdentity(value) {
                            const { email, phoneNumber } = value;

                            if (
                                isValidContactInfo(email, isEmailValid) &&
                                isValidContactInfo(phoneNumber, isPhoneNumberValid)
                            ) {
                                return true;
                            }

                            return this.createError({
                                path: `${this.path}.identity`,
                                message: createInvalidEmailOrPhoneText(),
                            });
                        })
                    );
                },
            }}
            render={function InnerRender({
                field: { value: incomingValue, onChange, settings },
                fieldState: { error },
            }) {
                const { rows, manager } = RowManager.useRowManager(
                    incomingValue ?? [],
                    (value) => {
                        onChange({
                            ...settings,
                            [subName]: value,
                        });
                    },
                    Infinity,
                    emptyRecipientRow,
                    {
                        placeholderRowStrategy: PLACEHOLDER_ROW_STRATEGY.ADD_WHEN_LAST_ROW_IS_DIRTY,
                    }
                );

                const ldenGuideline$ = useWatchObservable('ldenGuideline');
                const ldenGuideline = useObservableState(ldenGuideline$);

                const configuredAlarms = useMemo(
                    () => [
                        {
                            key: 'measuring_point_administrator',
                            title: gettext('ORIGINAL_CREATOR'),
                        },
                        ...alarms.flatMap(
                            (alarm) =>
                                settings[alarm.name].map((alarmField, index) => ({
                                    ...alarmField,
                                    title:
                                        alarmField.title === ''
                                            ? alarm.placeholder.replace('{index}', index + 1)
                                            : alarmField.title,
                                })) ?? []
                        ),
                        ...(!ldenGuideline || ldenGuideline === 'LdenGuidelineNone'
                            ? []
                            : [
                                  { key: 'lden_alarm', title: gettext('LDEN_ALARM') },
                                  { key: 'lden_warning', title: gettext('LDEN_WARNING') },
                              ]),
                    ],
                    [settings, ldenGuideline]
                );

                return (
                    <FormFieldContainer
                        fieldOptions={{
                            name: overrideName,
                            settings: {
                                // Although the label is hidden, it is used by the
                                // aria-label of the override checkbox.
                                label: gettext('ALARM_RECIPIENTS'),
                            },
                        }}
                        showLabel={false}
                        className={'inline-block'}
                        helpText={gettext('EMAIL_OR_PHONE_HINT')}
                    >
                        <div className="mt-24 text-sm">{gettext('ALARM_RECIPIENTS')}</div>
                        {/* The ID is needed for the walkthrough. */}
                        <table id="alarmRecipientSettings">
                            <thead>
                                <tr>
                                    <th className="p-0"></th>
                                    {configuredAlarms.map(({ key, title }) => (
                                        <th className="p-0" key={key}>
                                            <div className="translate-x-[-105px] translate-y-[-119px] rotate-45">
                                                <span className="absolute w-[165px] truncate border-b border-[#ccc] text-right">
                                                    {title}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <RowElement
                                        row={row}
                                        index={index}
                                        key={index}
                                        configuredAlarms={configuredAlarms}
                                        manager={manager}
                                        disabled={disabled}
                                        error={error?.[index]}
                                        overrideName={overrideName}
                                    />
                                ))}
                            </tbody>
                        </table>

                        <div>
                            <button
                                type="button"
                                onClick={() => !disabled && manager.addRow()}
                                className="cursor-pointer"
                            >
                                <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className="text-success"
                                    title={gettext('ADD_EMPTY_FIELD')}
                                />
                            </button>
                        </div>
                    </FormFieldContainer>
                );
            }}
        />
    );
}

AlarmRecipientsField.propTypes = {
    ...defaultPropTypes,
    extraValidators: PropTypes.object.isRequired,
};

function EmailDelayField({ fieldOptions, extraValidators }) {
    const { name } = fieldOptions;
    const subName = 'emailDelay';
    const overrideName = `${name}.${subName}`;
    const overridden = useIsOverridden(overrideName);
    const disabled = useIsOverridable(overrideName) && !overridden;

    return (
        <SubController
            extraValidators={extraValidators}
            subName={subName}
            fieldOptions={fieldOptions}
            rules={{
                buildValidationSchema() {
                    return buildYupSchema({
                        emailDelay: {
                            type: 'integer',
                            minValue: 10,
                            maxValue: 24 * 60 * 60,
                        },
                    }).fields.emailDelay;
                },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <FormFieldContainer
                    fieldOptions={{
                        name: overrideName,
                        settings: {
                            label: gettext('EMAIL_DELAY_LABEL'),
                        },
                    }}
                    inline={true}
                >
                    <OverridableInput
                        overrideName={overrideName}
                        id={`id_${overrideName}`}
                        type="number"
                        value={value}
                        disabled={disabled}
                        onChange={onChange}
                        error={error}
                    />
                </FormFieldContainer>
            )}
        />
    );
}

EmailDelayField.propTypes = {
    ...defaultPropTypes,
    extraValidators: PropTypes.object.isRequired,
};

export function NotificationConfigField({ fieldOptions }) {
    const { name, settings } = fieldOptions;
    /*
    Only one controller can do validation over a root element of the settings,
    so we let other components create SubControllers which fill their validation
    in extraValidators and create a controller to check those validations.
    The renders are done by the SubControllers themselves.
    */
    const extraValidators = {};

    return (
        <>
            <div className="flex flex-col gap-y-2.5 ">
                <AlarmLevelSettingsField
                    fieldOptions={fieldOptions}
                    extraValidators={extraValidators}
                />
                <h3 className="text-black">{gettext('ALARM_RECEPTION_SETTINGS')}</h3>
                <div className={inlineFieldGroupClasses}>
                    <AlarmRecipientsField
                        fieldOptions={fieldOptions}
                        extraValidators={extraValidators}
                    />
                    <EmailDelayField
                        fieldOptions={fieldOptions}
                        extraValidators={extraValidators}
                    />
                </div>
            </div>
            {/* Keep the Controller last so extraValidators will be properly
            populated at render. */}
            <Controller
                name={name}
                defaultValue={settings.default}
                rules={{
                    buildValidationSchema() {
                        return object(extraValidators);
                    },
                }}
                render={() => null}
            />
        </>
    );
}

NotificationConfigField.propTypes = defaultPropTypes;
